import Chip from "@mui/material/Chip"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import { styled } from "@mui/material/styles"
import { Variant } from "@/types/index"

export const colors = {
   grayBorder: "#E7EAEF",
   arrowIcon: "#7781A2",
   deleteIcon: "#147CCB",
   deleteIconHover: "#48A6DF",
   text: "#242F38",
   white: "#fff",
   softWhite: "#FCFCFD",
   disabledBg: "#D2D2D2",
   chipBg: "#F5F5F5",
   chipText: "#242F38",
   placeholder: "#808080",
   textMenuItemHover: "#48A6DF",
   textMenuItemSelected: "#147CCB",
   textMenuItemSelectedHover: "#48A6DF",
}

export const fonts = {
   rubik: "Rubik",
   dmSans: "DMSans",
}

export const sizeStyles = {
   [Variant.LARGE]: {
      fontSize: "1.313rem",
      fontWeight: "600",
      lineHeight: "1.61",
   },
   [Variant.DEFAULT]: {
      fontSize: "0.875rem",
      fontWeight: "300",
      lineHeight: "2.14",
   },
}

export const StyledFormControl = styled(FormControl)({
   margin: 0,
   minWidth: 100,
})

export const getStyledSelect = (size: Variant, chipSelection: boolean) =>
   styled(Select, {
      shouldForwardProp: (prop) =>
         !["size", "chipSelection", "disableUnderline"].includes(
            prop as string
         ),
   })(() => ({
      backgroundColor: size === Variant.LARGE ? colors.white : colors.softWhite,
      border: `1px solid ${colors.grayBorder}`,
      ...(!chipSelection
         ? {
              lineHeight: size === Variant.LARGE ? "" : "1.14",
              ...(size === Variant.LARGE && { height: "72px" }),
           }
         : {}),
      "&.MuiSelect-root": {
         fontFamily: size === Variant.LARGE ? fonts.dmSans : fonts.rubik,
         borderRadius: "4px",
         ".Mui-disabled": {
            backgroundColor: colors.disabledBg,
         },
         ".MuiOutlinedInput-notchedOutline": {
            border: "none",
         },
         ".MuiSelect-select": {
            boxSizing: "border-box",
            color: colors.text,
            fontSize: sizeStyles[size].fontSize,
            fontWeight: sizeStyles[size].fontWeight,
            ...(chipSelection
               ? {
                    padding: "3px",
                 }
               : {
                    padding: size === Variant.LARGE ? "12px 16px" : "7px 12px",
                    lineHeight: size === Variant.LARGE ? "0.95" : "",
                    ...(size === Variant.LARGE && {
                       height: "100%",
                       alignContent: "flex-end",
                    }),
                 }),
         },
         ".MuiSelect-icon": {
            right: "12px",
            top: "auto",
            color: colors.arrowIcon,
         },
      },
      "&.Mui-focused": {
         ".MuiOutlinedInput-notchedOutline": {
            border: "none",
         },
      },
   }))

export const StyledMenuItem = styled(MenuItem, {
   shouldForwardProp: (prop) =>
      !["size", "chipSelection"].includes(prop as string),
})(
   ({
      size = Variant.DEFAULT,
      chipSelection = false,
   }: {
      size: Variant
      chipSelection: boolean
   }) => ({
      padding: "0",
      fontSize: sizeStyles[size].fontSize,
      fontWeight: sizeStyles[size].fontWeight,
      lineHeight: sizeStyles[size].lineHeight,
      color: colors.text,
      display: "flex",
      alignItems: "center",
      columnGap: "0.625rem",
      fontFamily: size === Variant.LARGE ? fonts.dmSans : fonts.rubik,
      "*": {
         background: "unset !important",
      },
      "&:hover": {
         backgroundColor: "unset",
         color: colors.textMenuItemHover,
         "svg>path": {
            fill: colors.textMenuItemHover,
         },
      },
      "&.Mui-selected": {
         backgroundColor: "unset",
         ...(!chipSelection && {
            color: colors.textMenuItemSelected,
         }),
         "&.Mui-focusVisible": {
            backgroundColor: "unset",
         },
         "&:hover": {
            backgroundColor: "unset",
            color: colors.textMenuItemSelectedHover,
         },
      },
      "&.Mui-selected:has(:not(.Mui-disabled))": {
         backgroundColor: "unset",
      },
      "&:active": {
         backgroundColor: "unset",
      },
      "&.Mui-focusVisible": {
         backgroundColor: "unset",
      },
   })
)

export const StyledInputLabel = styled(InputLabel, {
   shouldForwardProp: (prop) => prop !== "size",
})(({ size = Variant.DEFAULT }: { size?: Variant }) => ({
   color: colors.text,
   fontSize: "0.875rem",
   fontWeight: 300,
   "&.Mui-focused": {
      color: colors.text,
   },
   "&.select__label": {
      fontFamily: fonts.rubik,
      transform: "translate(16px, 12px)",
   },
   "&.select__placeholder": {
      fontWeight: 400,
      color: colors.placeholder,
      transform: `translate(12px, ${size === Variant.LARGE ? "8px" : "6px"})`,
   },
}))

export const StyledChip = styled(Chip)({
   backgroundColor: colors.chipBg,
   color: colors.chipText,
   fontFamily: fonts.rubik,
   fontWeight: 400,
   fontSize: "0.875rem",
   height: "24px",
   borderRadius: "4px",
   padding: "4px 8px",
   gap: "8px",
   ".MuiChip-label": {
      paddingRight: "0",
      paddingLeft: "0",
   },
   ".MuiChip-deleteIcon": {
      margin: "0",
      color: colors.deleteIcon,
   },
   "&:hover": {
      backgroundColor: colors.chipBg,
      ".MuiChip-deleteIcon": {
         color: colors.deleteIconHover,
      },
   },
   "&:active": {
      boxShadow: "none",
   },
})
